import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

// One price per member. Keep in step with SEAT_PRICE in
// backend/src/services/seats.js and the seat price configured in Stripe.
const PLANS = [
  {
    name: 'Trial',
    price: 'Free',
    period: 'for 14 days',
    description: 'Everything, no card required to start.',
    features: [
      'Unlimited workspaces',
      'Unlimited shows',
      'vMix integration',
      'Singular.live integration',
      'Cue blocks',
      'Free guests',
      'Google Sheets import',
    ],
    cta: 'Start free trial',
    ctaPath: '/login?mode=register',
    highlight: false,
  },
  {
    name: 'Per seat',
    price: '£30',
    period: 'per member / month',
    description: 'For production teams of any size. Pay for the people in your account.',
    features: [
      'Unlimited workspaces',
      'Unlimited shows',
      'vMix integration',
      'Singular.live integration',
      'Cue blocks',
      'Free guests — commentators and freelancers, per workspace',
      'Seats follow your team, prorated',
      'Priority support',
    ],
    cta: 'Get started',
    ctaPath: '/login?mode=register',
    highlight: true,
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const token = useAuthStore(state => state.token);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-6 h-6 bg-red-600 rounded flex items-center justify-center">
              <div className="w-2.5 h-2.5 bg-white rounded-full" />
            </div>
            <span className="font-bold text-lg tracking-tight">Mission Control</span>
          </div>
          <h1 className="text-4xl font-bold mb-4">Simple, transparent pricing</h1>
          <p className="text-gray-400 text-lg">Start with a 14-day free trial. No card required.</p>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {PLANS.map(plan => (
            <div
              key={plan.name}
              className={`rounded-lg border p-6 flex flex-col ${
                plan.highlight
                  ? 'border-red-600 bg-red-900/10'
                  : 'border-[#2a2a2a] bg-[#111111]'
              }`}
            >
              <h2 className="text-xl font-bold mb-1">{plan.name}</h2>
              <p className="text-gray-500 text-sm mb-4">{plan.description}</p>

              <div className="mb-6">
                <span className="text-3xl font-bold">{plan.price}</span>
                <span className="text-gray-500 text-sm ml-2">{plan.period}</span>
              </div>

              <ul className="space-y-2 flex-1 mb-6">
                {plan.features.map(f => (
                  <li key={f} className="text-sm flex items-start gap-2 text-gray-300">
                    <span className="mt-0.5 flex-shrink-0 text-green-500">✓</span>
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => navigate(token ? '/billing' : plan.ctaPath)}
                className={`w-full py-2.5 rounded text-sm font-semibold transition-colors ${
                  plan.highlight
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : 'bg-[#1e1e1e] hover:bg-[#2a2a2a] text-white border border-[#2a2a2a]'
                }`}
              >
                {token ? 'Manage billing' : plan.cta}
              </button>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-600 text-xs mt-6">All prices exclude VAT.</p>

        {/* Footer links */}
        <div className="text-center mt-8 text-gray-600 text-sm">
          <button onClick={() => navigate(token ? '/' : '/login')} className="underline hover:text-gray-400">
            {token ? 'Back to dashboard' : 'Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
}
